import { Button, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Stack, Text, useToast } from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { updateCertificate, uploadFile } from "../../../../api";
import { colors } from "../../../../styles";
import { ICertificate } from "../../../../types";
import FileAttach from "../../../Auth/SignUp/FileAttach";
import { getSignUpErrorMessage } from "../../../Auth/SignUp/errors";
import { FieldMessage } from "../../../Auth/SignUp/StepLayout";

interface RejectedCertificateModalProps {
    isOpen: boolean;
    onClose: () => void;
    certificate: ICertificate;
}

export default function RejectedCertificateModal({ isOpen, onClose, certificate }: RejectedCertificateModalProps) {
    const queryClient = useQueryClient();
    const toast = useToast();
    const [certificateImage, setCertificateImage] = useState<File | null>(null);
    const [errorMessage, setErrorMessage] = useState("");

    const needsImage = !certificate.imageIsValid;

    useEffect(() => {
        if (isOpen) {
            setCertificateImage(null);
            setErrorMessage("");
        }
    }, [isOpen]);

    const uploadFileMutation = useMutation({ mutationFn: uploadFile });
    const updateCertificateMutation = useMutation({
        mutationFn: updateCertificate,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["me"] });
            toast({
                title: "증명서가 다시 제출되었습니다.",
                status: "success",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
            onClose();
        },
    });

    const onSubmit = async () => {
        if (needsImage && !certificateImage) {
            setErrorMessage("증명서 사진을 첨부해주세요.");
            return;
        }
        if (!needsImage) {
            setErrorMessage("수정할 증명서 정보가 없습니다.");
            return;
        }

        setErrorMessage("");

        try {
            const image = await uploadFileMutation.mutateAsync({ file: certificateImage as File });
            await updateCertificateMutation.mutateAsync({ image });
        } catch (error) {
            setErrorMessage(getSignUpErrorMessage(error, "증명서 보완 중 문제가 발생했습니다."));
        }
    };

    return (
        <Modal motionPreset="slideInBottom" isOpen={isOpen} onClose={onClose} size="xl">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    <Text fontSize={20} fontWeight={700} color={colors.black}>증명서 보완하기</Text>
                    <ModalCloseButton />
                </ModalHeader>
                <ModalBody padding={10}>
                    {certificate.rejectedReason && (
                        <Text fontSize={14} fontWeight={500} color={colors.apple} marginBottom={5}>
                            {certificate.rejectedReason}
                        </Text>
                    )}
                    <Text fontSize={12} fontWeight={500} color={colors.g3} marginBottom={5}>
                        유효하지 않은 항목만 다시 제출할 수 있습니다.
                    </Text>
                    {needsImage ? (
                        <FileAttach
                            label="01 | 재학증명서 또는 졸업증명서"
                            inputId="rejected-certificate-upload"
                            file={certificateImage}
                            onChange={(file) => {
                                setCertificateImage(file);
                                setErrorMessage("");
                            }}
                        />
                    ) : (
                        <Text fontSize={14} color={colors.g3}>보완할 항목이 없습니다.</Text>
                    )}
                    <FieldMessage message={errorMessage} />
                    <Stack marginTop={8} width="100%">
                        <Button
                            onClick={onSubmit}
                            isLoading={uploadFileMutation.isPending || updateCertificateMutation.isPending}
                            isDisabled={!needsImage || !certificateImage}
                            width="100%"
                            height="52px"
                            backgroundColor={colors.blue}
                            color={colors.white}
                            borderRadius={10}
                        >
                            제출
                        </Button>
                    </Stack>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
}
